/** axios instance for the Ops Console API. */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { config } from '@/config';

import { isDebugEnabled, recordDebugEntry, type DebugSummary } from './debugMode';
import { clearToken, getToken } from './tokenStorage';

export interface ConsoleApiErrorBody {
  detail?: string;
  errors?: string[];
  hints?: string[];
  /** Machine-readable discriminator, e.g. `statement_timeout`, `replica_conflict`. */
  code?: string;
  /** Statement budget (ms) that was exceeded, on `statement_timeout` errors. */
  timeout_ms?: number;
  /** Required config keys with no value, on `missing_credentials` errors. */
  missing_keys?: string[];
}

export class ConsoleApiError extends Error {
  public override readonly name = 'ConsoleApiError';
  constructor(
    public readonly status: number,
    public readonly body: ConsoleApiErrorBody | undefined,
    message: string,
  ) {
    super(message);
  }
}

/** The integration has no usable credentials for this account, so nothing was
 *  asked of the vendor. Actionable: the caller can send the operator straight to
 *  the integration's config page instead of showing a dead-end message.
 *  Not a type predicate, for the same reason as isStatementTimeout below. */
export function isMissingCredentials(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 400 && e.body?.code === 'missing_credentials';
}

/** A cache-only read found nothing cached — e.g. the product catalogue expired
 *  or was evicted between its fetch finishing and the page reading it. The
 *  caller offers to fetch again. Not a type predicate (see isStatementTimeout). */
export function isCatalogueNotLoaded(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 409 && e.body?.code === 'catalogue_not_loaded';
}

/** The backend cancelled the search because it exceeded its statement budget.
 *  Deliberately NOT a type predicate: `!isStatementTimeout(e)` must not
 *  narrow a ConsoleApiError to `never` in the generic-400 branches. */
export function isStatementTimeout(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 400 && e.body?.code === 'statement_timeout';
}

/**
 * HTTP budget for `timeout=extended` list requests. The 60s statement budget
 * applies per statement (page query + count probe can each take up to 60s),
 * so the transport budget must cover ~2× the tier plus slack — still far
 * under the 300s gunicorn/ALB kill lines.
 */
export const EXTENDED_LIST_HTTP_TIMEOUT_MS = 130_000;

/** Axios config for a list call, widening the HTTP budget on extended-tier
 *  requests. Spread into `apiGet(url, extendedListConfig(p.timeout))`. */
export function extendedListConfig(timeout?: 'extended'): AxiosRequestConfig | undefined {
  return timeout === 'extended' ? { timeout: EXTENDED_LIST_HTTP_TIMEOUT_MS } : undefined;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export const client: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

/** Present on the config iff the request went out with `X-CP-Debug: 1`. */
interface DebugStampedConfig extends InternalAxiosRequestConfig {
  cpDebugStart?: number;
}

client.interceptors.request.use((req) => {
  const token = getToken();
  if (token && !req.headers?.has?.('Authorization')) {
    req.headers = req.headers ?? {};
    req.headers.Authorization = `Token ${token}`;
  }
  if (isDebugEnabled()) {
    req.headers['X-CP-Debug'] = '1';
    (req as DebugStampedConfig).cpDebugStart = performance.now();
  }
  return req;
});

function headerValue(headers: unknown, name: string): string | null {
  const h = headers as { get?: (n: string) => unknown } & Record<string, unknown>;
  const value = typeof h?.get === 'function' ? h.get(name) : h?.[name];
  return typeof value === 'string' ? value : null;
}

/** Never let credentials leave the app inside a HAR export. */
const REDACTED_HEADERS = new Set(['authorization', 'x-enrollment-token', 'cookie', 'set-cookie']);
/** Per-entry response-body cap for HAR capture (chars). */
const MAX_BODY_CHARS = 256 * 1024;

function collectHeaders(headers: unknown): Record<string, string> {
  const h = headers as { toJSON?: () => unknown } | undefined;
  const obj = typeof h?.toJSON === 'function' ? h.toJSON() : headers;
  const out: Record<string, string> = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const [name, value] of Object.entries(obj)) {
    if (value == null || typeof value === 'function') continue;
    out[name] = REDACTED_HEADERS.has(name.toLowerCase()) ? '<redacted>' : String(value);
  }
  return out;
}

function serializeBody(data: unknown): { text: string | null; size: number; truncated: boolean } {
  if (data == null || data === '') return { text: null, size: 0, truncated: false };
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return { text: null, size: data.size, truncated: false };
  }
  if (data instanceof ArrayBuffer) return { text: null, size: data.byteLength, truncated: false };
  let text: string;
  if (typeof data === 'string') {
    text = data;
  } else {
    try {
      text = JSON.stringify(data);
    } catch {
      return { text: null, size: -1, truncated: false };
    }
  }
  if (text.length > MAX_BODY_CHARS) {
    return { text: text.slice(0, MAX_BODY_CHARS), size: text.length, truncated: true };
  }
  return { text, size: text.length, truncated: false };
}

/** Record a debug-flagged request for the toolbar. Extracts the backend
 *  summary from the body's `_debug` key — and strips it, so callers and the
 *  React Query cache see exactly the OpenAPI-generated shapes — or from the
 *  compact `X-CP-Debug-Summary` header on non-object responses. */
function maybeRecordDebug(
  cfg: InternalAxiosRequestConfig | undefined,
  status: number | null,
  statusText: string | null,
  headers: unknown,
  data: unknown,
): void {
  const start = (cfg as DebugStampedConfig | undefined)?.cpDebugStart;
  if (cfg === undefined || start === undefined) return;

  let summary: DebugSummary | null = null;
  if (data && typeof data === 'object' && !Array.isArray(data) && '_debug' in data) {
    summary = (data as { _debug: DebugSummary })._debug;
    delete (data as Record<string, unknown>)._debug;
  } else {
    const compact = headerValue(headers, 'x-cp-debug-summary');
    if (compact) {
      try {
        summary = JSON.parse(compact) as DebugSummary;
      } catch {
        // Malformed header — keep the transport row without a summary.
      }
    }
  }

  let url = cfg.url ?? '';
  let path = url;
  let query = '';
  try {
    const parsed = new URL(client.getUri(cfg), window.location.origin);
    url = parsed.toString();
    path = parsed.pathname;
    query = parsed.search.replace(/^\?/, '');
  } catch {
    // Unparseable URL — fall back to the raw config url.
  }

  // Serialized AFTER the _debug strip, so the HAR body matches what callers saw.
  const body = serializeBody(data);

  recordDebugEntry({
    method: (cfg.method ?? 'get').toUpperCase(),
    url,
    path,
    query,
    status,
    statusText,
    durationMs: Math.round(performance.now() - start),
    requestId: headerValue(headers, 'x-request-id'),
    requestHeaders: collectHeaders(cfg.headers),
    responseHeaders: collectHeaders(headers),
    responseBody: body.text,
    responseBodySize: body.size,
    responseBodyTruncated: body.truncated,
    responseMimeType: headerValue(headers, 'content-type'),
    summary,
  });
}

client.interceptors.response.use(
  (res) => {
    maybeRecordDebug(res.config, res.status, res.statusText, res.headers, res.data);
    return res;
  },
  (err: AxiosError<ConsoleApiErrorBody>) => {
    maybeRecordDebug(
      err.config,
      err.response?.status ?? null,
      err.response?.statusText ?? null,
      err.response?.headers,
      err.response?.data,
    );

    const status = err.response?.status ?? 0;
    const body = err.response?.data;
    const url = err.config?.url ?? '';

    // 401 on a non-login endpoint → session is bad; clear + redirect.
    if (status === 401 && !url.includes('/auth/login/') && !url.includes('/auth/mfa/verify/')) {
      clearToken();
      onUnauthorized?.();
    }

    const message = body?.detail || err.message || `HTTP ${status}`;
    return Promise.reject(new ConsoleApiError(status, body, message));
  },
);

/** Axios config carrying an `X-Change-Reason` header — the free-text "why"
 *  stored on the backend's change-history rows for this mutation. Spread-safe:
 *  returns undefined for an empty reason. */
export function withChangeReason(
  reason: string | null | undefined,
  base?: AxiosRequestConfig,
): AxiosRequestConfig | undefined {
  const trimmed = reason?.trim();
  if (!trimmed) return base;
  return { ...base, headers: { ...base?.headers, 'X-Change-Reason': trimmed.slice(0, 512) } };
}

/** Convenience wrappers — typed call sites import the generated `paths`. */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await client.get<T>(url, config);
  return r.data;
}
export async function apiPost<T>(url: string, body?: unknown, cfg?: AxiosRequestConfig): Promise<T> {
  const r = await client.post<T>(url, body, cfg);
  return r.data;
}
export async function apiPatch<T>(url: string, body?: unknown, cfg?: AxiosRequestConfig): Promise<T> {
  const r = await client.patch<T>(url, body, cfg);
  return r.data;
}
export async function apiPut<T>(url: string, body?: unknown, cfg?: AxiosRequestConfig): Promise<T> {
  const r = await client.put<T>(url, body, cfg);
  return r.data;
}
export async function apiDelete<T>(url: string, cfg?: AxiosRequestConfig): Promise<T> {
  const r = await client.delete<T>(url, cfg);
  return r.data;
}
